import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams, useOutletContext, NavLink, Navigate, Outlet } from 'react-router-dom'
import { ArrowLeft, Building2, Store, ClipboardList, History, Package, Info, Key } from 'lucide-react'
import toast from 'react-hot-toast'
import carteraService from '@/services/carteraService'
import PageContainer from '@/components/common/PageContainer'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import Resumen from './ficha/Resumen'
import { dinero } from './ficha/formato'
import PaqueteYAlcance from './ficha/PaqueteYAlcance'
import Gestion from './ficha/Gestion'
import Empresas from './ficha/Empresas'
import Solicitudes from './ficha/Solicitudes'
import Historial from './ficha/Historial'

/**
 * Ficha del cliente (docs/71 P9) + Addenda de cambio de paquete (docs/71 P15).
 *
 * La lista da una fila por empresa; aquí se abre UNA y se ve lo que la lista no puede pagar: el
 * alcance real del tenant (incluida la ocultación del dueño), y desde aquí se cambia el paquete
 * —lo único del flujo que mueve dinero, por eso el panel de impacto muestra precio Y comisión
 * antes de generar nada—.
 *
 * Cada pestaña es una SUBRUTA (`/clientes/:subId/paquete`): recargar, compartir el enlace o volver
 * atrás no la pierde. Este archivo es el marco —cabecera, barra de pestañas y la carga de la
 * ficha— y cada pestaña vive en `ficha/`. Los enlaces viejos `?tab=x` se redirigen a la subruta.
 */

const ESTADOS = {
  active: { label: 'Activa', variant: 'success' },
  trialing: { label: 'En prueba', variant: 'warning' },
  past_due: { label: 'Con mora', variant: 'danger' },
  paused: { label: 'Pausada', variant: 'secondary' },
  canceled: { label: 'Cancelada', variant: 'secondary' },
  expired: { label: 'Prueba vencida', variant: 'secondary' },
  incomplete: { label: 'Incompleta', variant: 'warning' },
}

const PESTANAS = [
  { clave: 'resumen', label: 'Resumen', Icono: Info },
  { clave: 'paquete', label: 'Paquete y alcance', Icono: Package },
  { clave: 'gestion', label: 'Gestión', Icono: Key },
  { clave: 'empresas', label: 'Empresas', Icono: Store },
  { clave: 'solicitudes', label: 'Solicitudes', Icono: ClipboardList },
  { clave: 'historial', label: 'Historial', Icono: History },
]
const PESTANA_INICIAL = 'resumen'
const esPestana = (v) => PESTANAS.some((p) => p.clave === v)

export default function ClienteFicha() {
  const { subId } = useParams()
  const navigate = useNavigate()
  const [ficha, setFicha] = useState(null)
  const [loading, setLoading] = useState(true)

  const cargar = useCallback(async () => {
    setLoading(true)
    try {
      const res = await carteraService.getFicha(subId)
      setFicha(res?.data || null)
    } catch {
      toast.error('No se pudo cargar la ficha')
      setFicha(null)
    } finally {
      setLoading(false)
    }
  }, [subId])

  useEffect(() => { cargar() }, [cargar])

  if (loading) {
    return (
      <PageContainer>
        <Skeleton className="h-8 w-64 mb-4" />
        <Card className="p-6"><Skeleton className="h-40" /></Card>
      </PageContainer>
    )
  }
  if (!ficha) {
    return (
      <PageContainer>
        <Button variant="ghost" onClick={() => navigate('/clientes')}><ArrowLeft size={16} className="mr-1.5" /> Volver</Button>
        <Card className="p-8 text-center text-neutral-500 dark:text-neutral-400 mt-4">No se encontró esta empresa en tu cartera.</Card>
      </PageContainer>
    )
  }

  const est = ESTADOS[ficha.estado] || { label: ficha.estado, variant: 'secondary' }
  // «Plan · modalidad · mensual»: lo primero que el asesor quiere confirmar al abrir la ficha.
  const lineaPlan = [
    ficha.edicion,
    ficha.modalidad,
    ficha.mrr ? `${dinero(ficha.mrr, ficha.moneda)}/mes` : null,
  ].filter(Boolean).join(' · ')

  return (
    <PageContainer>
      <div>
        <button
          onClick={() => navigate('/clientes')}
          className="inline-flex items-center gap-1.5 text-body-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-800 dark:hover:text-neutral-200 mb-3"
        >
          <ArrowLeft size={16} /> Mis clientes
        </button>

        {/* En el teléfono: nombre arriba (puede ocupar dos líneas), y debajo una sola línea que
            se acomoda sola con el plan, el estado y la marca de demo. Desde sm, el estado vuelve
            a la derecha. */}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="font-display text-h2 sm:text-h1 font-semibold text-foreground leading-tight flex items-start gap-2">
              <Building2 size={22} className="mt-0.5 sm:mt-1 shrink-0 text-neutral-400 dark:text-neutral-500" />
              <span className="min-w-0 break-words">{ficha.empresaNombre || 'Sin nombre'}</span>
            </h2>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm text-subtle">
              {lineaPlan && <span className="min-w-0">{lineaPlan}</span>}
              <Badge variant={est.variant} className="sm:hidden">{est.label}</Badge>
              {ficha.esDemo && <Badge variant="warning">DEMO</Badge>}
            </div>
            <p className="text-body-sm text-subtle mt-1 break-all">{ficha.dueño?.email || '—'}</p>
          </div>
          <Badge variant={est.variant} className="hidden sm:inline-flex shrink-0">{est.label}</Badge>
        </div>
      </div>

      {/*
        Una sola fila con desplazamiento horizontal: con `flex-wrap` y alto fijo, en móvil se
        cortaban. En el teléfono la barra sangra hasta los bordes (-mx-4) para que la última
        pestaña se vea asomar y se entienda que hay más a la derecha.
      */}
      <nav aria-label="Secciones de la ficha" className="-mx-4 overflow-x-auto no-scrollbar px-4 sm:mx-0 sm:px-0">
        <ul className="flex w-max min-w-full gap-1 border-b border-line">
          {PESTANAS.map(({ clave, label, Icono }) => (
            <li key={clave} className="shrink-0">
              <NavLink
                to={clave}
                replace
                className={({ isActive }) => cn(
                  'inline-flex min-h-[44px] items-center whitespace-nowrap border-b-2 -mb-px px-4 py-2 text-body-sm font-medium transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm',
                  isActive
                    ? 'border-primary text-primary dark:text-primary-300'
                    : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50',
                )}
              >
                <Icono size={14} className="mr-1.5" /> {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <Outlet context={{ ficha, cargar }} />
    </PageContainer>
  )
}

/**
 * `/clientes/:subId` sin pestaña. Respeta los enlaces viejos `?tab=x` (marcadores, correos,
 * otras pantallas) llevándolos a `/clientes/:subId/x`; sin `tab`, a la pestaña inicial.
 */
export function FichaInicio() {
  const [busqueda] = useSearchParams()
  const tab = busqueda.get('tab')
  return <Navigate to={esPestana(tab) ? tab : PESTANA_INICIAL} replace />
}

/** La pestaña de `/clientes/:subId/:tab`. Una pestaña desconocida vuelve a la inicial. */
export function FichaPestana() {
  const { tab } = useParams()
  const { ficha, cargar } = useOutletContext()
  switch (tab) {
    case 'resumen': return <Resumen ficha={ficha} />
    case 'paquete': return <PaqueteYAlcance ficha={ficha} onCambio={cargar} />
    case 'gestion': return <Gestion ficha={ficha} onCambio={cargar} />
    case 'empresas': return <Empresas ficha={ficha} />
    case 'solicitudes': return <Solicitudes ficha={ficha} />
    case 'historial': return <Historial ficha={ficha} onCambio={cargar} />
    default: return <Navigate to={`../${PESTANA_INICIAL}`} replace />
  }
}
